import React, { useState, useEffect } from 'react'
import { useTenant } from '../../contexts/TenantContext'
import CompactPlayerForm from '../../components/client-admin/CompactPlayerForm'
import api from '../../services/apiService'
import '../../styles/client-admin/player-state-wise-view.scss'

// All Indian States
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
]

const PlayerStateWiseView = () => {
  const { tenant } = useTenant()
  const [loading, setLoading] = useState(true)
  const [currentView, setCurrentView] = useState('states') // 'states', 'players', 'detail'
  const [allPlayers, setAllPlayers] = useState([])
  const [stateData, setStateData] = useState([])
  const [selectedState, setSelectedState] = useState(null)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [showEditForm, setShowEditForm] = useState(false)

  useEffect(() => {
    loadPlayers()
  }, [])

  const loadPlayers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/client-admin/players')
      const result = response.data

      if (result.success) {
        const playersData = result.data || []
        setAllPlayers(playersData)
        calculateStateData(playersData)
      }
    } catch (error) {
      console.error('Error loading players:', error)
    } finally {
      setLoading(false)
    }
  }

  const calculateStateData = (players) => {
    const stateMap = {}

    // Initialize all Indian states with 0 count
    INDIAN_STATES.forEach(state => {
      stateMap[state] = {
        name: state,
        count: 0,
        players: []
      }
    })

    // Count players per state
    players.forEach(player => {
      const state = player.state || 'Unknown'
      if (stateMap[state]) {
        stateMap[state].count++
        stateMap[state].players.push(player)
      } else {
        // Handle unknown states
        if (!stateMap['Unknown']) {
          stateMap['Unknown'] = { name: 'Unknown', count: 0, players: [] }
        }
        stateMap['Unknown'].count++
        stateMap['Unknown'].players.push(player)
      }
    })

    const stats = Object.values(stateMap).sort((a, b) => a.name.localeCompare(b.name))
    setStateData(stats)
  }

  const handleStateClick = (state) => {
    if (state.count === 0) {
      alert(`No players found in ${state.name}`)
      return
    }
    setSelectedState(state)
    setCurrentView('players')
  }

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player)
    setCurrentView('detail')
  }

  const handleBackToStates = () => {
    setCurrentView('states')
    setSelectedState(null)
    setSelectedPlayer(null)
  }

  const handleBackToPlayers = () => {
    setCurrentView('players')
    setSelectedPlayer(null)
  }

  const handleEdit = (player) => {
    setSelectedPlayer(player)
    setShowEditForm(true)
  }

  const handleDelete = async (playerId) => {
    if (!confirm('Are you sure you want to delete this player?')) return

    try {
      const response = await api.delete(`/client-admin/players/${playerId}`)
      const result = response.data
      if (result.success) {
        alert('Player deleted successfully')
        await loadPlayers()
        handleBackToPlayers()
      }
    } catch (error) {
      console.error('Error deleting player:', error)
      alert('Failed to delete player')
    }
  }

  const handleFormSuccess = () => {
    setShowEditForm(false)
    loadPlayers()
  }

  if (loading) {
    return (
      <div className="player-state-view">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading players...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="player-state-view">
      {/* Header */}
      <div className="view-header">
        <div className="header-icon">
          <i className="fas fa-users"></i>
        </div>
        <div className="header-content">
          <h1>Player Management - State Wise</h1>
          <p>View players state-wise, check details, edit and delete records</p>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <button
          className={currentView === 'states' ? 'active' : ''}
          onClick={handleBackToStates}
        >
          <i className="fas fa-map-marked-alt"></i>
          All States
        </button>
        {currentView !== 'states' && (
          <>
            <i className="fas fa-chevron-right"></i>
            <button
              className={currentView === 'players' ? 'active' : ''}
              onClick={handleBackToPlayers}
            >
              <i className="fas fa-users"></i>
              {selectedState?.name} ({selectedState?.count})
            </button>
          </>
        )}
        {currentView === 'detail' && (
          <>
            <i className="fas fa-chevron-right"></i>
            <button className="active">
              <i className="fas fa-user"></i>
              {selectedPlayer?.fullName}
            </button>
          </>
        )}
      </div>

      {/* View 1: States Table */}
      {currentView === 'states' && (
        <div className="states-table-view">
          <div className="table-header">
            <h2>Indian States & Union Territories</h2>
            <div className="total-count">
              <i className="fas fa-users"></i>
              Total Players: {allPlayers.length}
            </div>
          </div>

          <div className="table-container">
            <table className="states-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>State / Union Territory</th>
                  <th>Player Count</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stateData.map((state, index) => (
                  <tr
                    key={state.name}
                    className={state.count > 0 ? 'has-players' : 'no-players'}
                  >
                    <td>{index + 1}</td>
                    <td className="state-name">
                      <i className="fas fa-map-marker-alt"></i>
                      {state.name}
                    </td>
                    <td className="player-count">
                      <span className={state.count > 0 ? 'badge-success' : 'badge-default'}>
                        {state.count} {state.count === 1 ? 'Player' : 'Players'}
                      </span>
                    </td>
                    <td className="actions">
                      {state.count > 0 ? (
                        <button
                          className="btn-view"
                          onClick={() => handleStateClick(state)}
                        >
                          <i className="fas fa-eye"></i>
                          View Players
                        </button>
                      ) : (
                        <span className="no-data">No Players</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 2: Players List for Selected State */}
      {currentView === 'players' && selectedState && (
        <div className="players-table-view">
          <div className="table-header">
            <h2>Players from {selectedState.name}</h2>
            <div className="total-count">
              <i className="fas fa-users"></i>
              {selectedState.count} {selectedState.count === 1 ? 'Player' : 'Players'}
            </div>
          </div>

          <div className="table-container">
            <table className="players-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reg ID</th>
                  <th>Full Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>City</th>
                  <th>Mobile</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedState.players.map((player, index) => (
                  <tr key={player.id}>
                    <td>{index + 1}</td>
                    <td className="reg-id">{player.regId}</td>
                    <td className="player-name">
                      <i className="fas fa-user-circle"></i>
                      {player.fullName}
                    </td>
                    <td>{player.age}</td>
                    <td>{player.gender}</td>
                    <td>{player.city}</td>
                    <td>{player.mobile}</td>
                    <td className="actions">
                      <button
                        className="btn-detail"
                        onClick={() => handlePlayerClick(player)}
                      >
                        <i className="fas fa-info-circle"></i>
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 3: Player Detail */}
      {currentView === 'detail' && selectedPlayer && (
        <div className="player-detail-view">
          <div className="detail-header">
            <div className="player-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            <div className="player-info">
              <h2>{selectedPlayer.fullName}</h2>
              <p className="reg-id">Registration ID: {selectedPlayer.regId}</p>
              <p className="state"><i className="fas fa-map-marker-alt"></i> {selectedPlayer.state}</p>
            </div>
            <div className="header-actions">
              <button className="btn-edit" onClick={() => handleEdit(selectedPlayer)}>
                <i className="fas fa-edit"></i>
                Edit Player's Detail
              </button>
              <button className="btn-delete" onClick={() => handleDelete(selectedPlayer.id)}>
                <i className="fas fa-trash-alt"></i>
                Delete Player
              </button>
            </div>
          </div>

          <div className="detail-body">
            <div className="detail-section">
              <h3><i className="fas fa-user"></i> Personal Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <div className="value">{selectedPlayer.fullName}</div>
                </div>
                <div className="detail-item">
                  <label>Father's Name</label>
                  <div className="value">{selectedPlayer.fatherName}</div>
                </div>
                <div className="detail-item">
                  <label>Date of Birth</label>
                  <div className="value">{selectedPlayer.dob}</div>
                </div>
                <div className="detail-item">
                  <label>Age</label>
                  <div className="value">{selectedPlayer.age} years</div>
                </div>
                <div className="detail-item">
                  <label>Gender</label>
                  <div className="value">{selectedPlayer.gender}</div>
                </div>
                <div className="detail-item">
                  <label>Marital Status</label>
                  <div className="value">{selectedPlayer.maritalStatus}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-map-marked-alt"></i> Address Information</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Address</label>
                  <div className="value">{selectedPlayer.address}</div>
                </div>
                <div className="detail-item">
                  <label>City</label>
                  <div className="value">{selectedPlayer.city}</div>
                </div>
                <div className="detail-item">
                  <label>State</label>
                  <div className="value">{selectedPlayer.state}</div>
                </div>
                <div className="detail-item">
                  <label>Pin Code</label>
                  <div className="value">{selectedPlayer.pinCode}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-phone"></i> Contact Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <div className="value">{selectedPlayer.email}</div>
                </div>
                <div className="detail-item">
                  <label>Mobile Number</label>
                  <div className="value">{selectedPlayer.mobile}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-baseball-ball"></i> Player Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Position</label>
                  <div className="value">{selectedPlayer.position}</div>
                </div>
                <div className="detail-item">
                  <label>Batting Style</label>
                  <div className="value">{selectedPlayer.battingStyle}</div>
                </div>
                <div className="detail-item">
                  <label>Bowling Style</label>
                  <div className="value">{selectedPlayer.bowlingStyle}</div>
                </div>
                <div className="detail-item">
                  <label>Height</label>
                  <div className="value">{selectedPlayer.height} cm</div>
                </div>
                <div className="detail-item">
                  <label>Weight</label>
                  <div className="value">{selectedPlayer.weight} kg</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedPlayer && (
        <CompactPlayerForm
          player={selectedPlayer}
          onClose={() => setShowEditForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default PlayerStateWiseView
